<template>
  <ModalPanel :open="modelValue" :initial-focus="nameInput" @close="close">
    <form novalidate @submit.prevent="submit">
      <DialogTitle as="h3" class="text-base font-semibold text-gray-900 dark:text-white">
        Create organization
      </DialogTitle>
      <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
        Share runners with your team. You become the owner; the new organization starts without a
        plan.
      </p>

      <div class="mt-5">
        <label
          for="organization-name"
          class="block text-sm font-medium text-gray-900 dark:text-white"
          >Name</label
        >
        <input
          id="organization-name"
          ref="nameInput"
          v-model="name"
          type="text"
          :maxlength="ORGANIZATION_NAME_MAX_LENGTH"
          required
          autocomplete="organization"
          placeholder="e.g. Platform team"
          :aria-invalid="Boolean(error)"
          :aria-describedby="error ? 'organization-name-error' : undefined"
          :disabled="submitting"
          class="mt-1 block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 disabled:cursor-not-allowed disabled:bg-gray-50 sm:text-sm/6 dark:bg-white/5 dark:text-white dark:outline-gray-700 dark:placeholder:text-gray-500 dark:focus:outline-indigo-500"
          :class="error ? 'outline-red-500 dark:outline-red-400' : ''"
        />
        <p
          v-if="error"
          id="organization-name-error"
          class="mt-1 text-sm text-red-600 dark:text-red-400"
          role="alert"
        >
          {{ error }}
        </p>
      </div>

      <div class="mt-6 sm:flex sm:flex-row-reverse gap-3">
        <button
          type="submit"
          :disabled="submitting"
          class="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-indigo-500 disabled:cursor-wait disabled:opacity-50 sm:w-auto dark:bg-indigo-500 dark:hover:bg-indigo-400 cursor-pointer"
        >
          {{ submitting ? 'Creating…' : 'Create organization' }}
        </button>
        <button
          type="button"
          :disabled="submitting"
          class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs inset-ring inset-ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto dark:bg-white/10 dark:text-white dark:inset-ring-white/5 dark:hover:bg-white/20 cursor-pointer"
          @click="close"
        >
          Cancel
        </button>
      </div>
    </form>
  </ModalPanel>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { DialogTitle } from '@headlessui/vue';
import ModalPanel from '@/components/ModalPanel.vue';
import { useOrganizationStore } from '@/stores/organization';
import {
  ORGANIZATION_NAME_MAX_LENGTH,
  organizationApiError,
  validateOrganizationName,
} from '@/lib/organizations';
import type { Organization } from '@/api/organizations';

const props = defineProps<{ modelValue: boolean }>();
const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'created', organization: Organization): void;
}>();

const organizations = useOrganizationStore();
const name = ref('');
const error = ref<string | null>(null);
const submitting = ref(false);
const nameInput = ref<HTMLInputElement | null>(null);

watch(
  () => props.modelValue,
  (open) => {
    if (open) {
      name.value = '';
      error.value = null;
    }
  }
);

function close() {
  if (submitting.value) return;
  emit('update:modelValue', false);
}

async function submit() {
  error.value = validateOrganizationName(name.value);
  if (error.value) {
    nameInput.value?.focus();
    return;
  }
  submitting.value = true;
  try {
    const created = await organizations.create(name.value.trim());
    emit('update:modelValue', false);
    emit('created', created);
  } catch (e) {
    error.value = organizationApiError(e, 'create the organization').message;
  } finally {
    submitting.value = false;
  }
}
</script>
